import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  OnInit,
  Output,
  ViewChild,
  Input,
  OnChanges,
  SimpleChanges,
} from "@angular/core";
import { NgSelectComponent } from "@ng-select/ng-select";
import { concat, merge, Observable, of, Subject } from "rxjs";
import {
  debounceTime,
  distinctUntilChanged,
  mapTo,
  switchMap,
  tap,
} from "rxjs/operators";
import {
  DataService,
  LogicalOperator,
  SharedModule,
  SortOrder,
} from "@bb-vendure/admin-ui/core";
import { GetAllProductVariantsDocument } from "../generated-graphql/graphql";

/**
 * @description
 * A component for selecting product variants via an autocomplete-style select input.
 *
 * @example
 * ```HTML
 * <vdr-product-variant-selector
 *   (productSelected)="selectResult($event)"></vdr-product-selector>
 * ```
 *
 * @docsCategory components
 */
@Component({
  standalone: true,
  imports: [SharedModule],
  selector: "product-variant-selector",
  templateUrl: "./product-variant-selector.component.html",
  styleUrls: ["./product-variant-selector.component.scss"],
  changeDetection: ChangeDetectionStrategy.Default,
})
export class ProductVariantSelectorComponent implements OnInit, OnChanges {
  searchInput$ = new Subject<string>();
  searchLoading = false;
  searchResults$: Observable<any>;
  _value: any;
  @Output() productSelected = new EventEmitter<any>();

  @Input()
  productId: string;

  @Input()
  freeGifts: boolean;

  @ViewChild("autoComplete", { static: true })
  private ngSelect: NgSelectComponent;
  constructor(private dataService: DataService) {}
  ngOnChanges(changes: SimpleChanges): void {
    console.log(this.productId);

    if (this.productId) {
      this.dataService.product
        .getProductVariantsForProduct({}, this.productId)
        .mapSingle((res) => {
          this.variantList = res.productVariants.items;
          this.initSearchResults();
        });
    }
  }

  ngOnInit(): void {
    if (this.productId) {
      this.dataService.product
        .getProductVariantsForProduct({}, this.productId)
        .single$.pipe()
        .subscribe((res) => {
          this.variantList = res.productVariants.items;
          this.initSearchResults();
        });
    } else {
      this.initSearchResults();
    }
  }

  variantList: any[] = [];

  private initSearchResults() {
    const searchItems$ = this.searchInput$.pipe(
      debounceTime(200),
      distinctUntilChanged(),
      tap(() => (this.searchLoading = true)),
      switchMap((term) => {
        if (!term) {
          if (this.productId) {
            if (this.variantList.length > 0) {
              return this.variantList;
            }

            return this.dataService.product
              .getProductVariantsForProduct(
                {
                  filter: {
                    name: {
                      contains: term,
                    },
                    sku: {
                      contains: term,
                    },
                  },
                  filterOperator: LogicalOperator.OR,
                },
                this.productId
              )
              .mapSingle((result) => {
                this.variantList = result.productVariants.items;
                return result.productVariants.items;
              });
          }

          return this.dataService
            .query(GetAllProductVariantsDocument, {
              options: {
                take: 10,
                sort: {
                  createdAt: SortOrder.DESC as any,
                },
              },
              isFreeGift: this.freeGifts ?? undefined,
            })
            .mapSingle((result) => result.productVariantsFilterFreeGift.items);
        }

        if (this.productId) {
          if (this.variantList.length > 0) {
            return this.variantList;
          }

          return this.dataService.product
            .getProductVariantsForProduct(
              {
                filter: {
                  name: {
                    contains: term,
                  },
                  sku: {
                    contains: term,
                  },
                },
                filterOperator: LogicalOperator.OR,
              },
              this.productId
            )
            .mapSingle((result) => {
              this.variantList = result.productVariants.items;
              return result.productVariants.items;
            });
        }

        return this.dataService
          .query(GetAllProductVariantsDocument, {
            options: {
              filter: {
                name: {
                  contains: term,
                },
                sku: {
                  contains: term,
                },
              },
              filterOperator: "OR" as any,
            },
            isFreeGift: this.freeGifts ?? undefined,
          })
          .mapSingle((result) => result.productVariantsFilterFreeGift.items);
      }),
      tap(() => (this.searchLoading = false))
    );

    const clear$ = this.productSelected.pipe(mapTo([]));

    this.searchResults$ = concat(
      of(this.variantList),
      searchItems$
      //   merge(, clear$)
    );
  }

  selectResult(product?: any) {
    if (product) {
      this.productSelected.emit(product);
      //this._value = null;
    }
  }
}
